import { Stack } from "@mui/material";
import { useParams } from "react-router-dom";
import { useEffect, useState } from "react";
import { ToTopBtn } from "../components/ui/buttons";
import { PlaybookHeader } from "../components/company/plan";
import {
  CRMNotes,
  DoDontBlock,
  FirstCallScriptBlock,
  LeadAmnesiaBestPracticesBlock,
  LeadDetailsBlock,
  OutboundSequenceBlock,
  PreOutreachResearchBlock,
  ReadFirstBlock,
  TextBlock,
} from "../components/company/plan/component";
import { PlaybookNavigationBlock } from "../components/company/plan/component/PlaybookNavigationBlock";
import { useGetPlaybookQuery } from "../api";
import { useNotFoundRedirect } from "../hooks";
import { CustomLoader } from "../components/ui/CustomLoader";
import {
  playbookAndrewDavis,
  playbookStephanieKramer,
  playbookVaidyVenkatesan,
} from "../mock";
import { Playbook } from "../types";

const mokPlaybook: Record<string, Playbook> = {
  "3f095073-9b96-4327-9cd7-3c04ff3d501b": playbookAndrewDavis,
  "3f095073-9b96-4327-9cd7-3c04ff3d502b": playbookStephanieKramer,
  "3f095073-9b96-4327-9cd7-3c04ff3d503b": playbookVaidyVenkatesan,
};

const PublicPlaybookPage = () => {
  const { member_id, campaign_id } = useParams();
  const currentPlaybook = member_id ? mokPlaybook[member_id] : undefined;

  const [showScrollTop, setShowScrollTop] = useState<boolean>(false);
  const [topicsList, setTopicsList] = useState<string[]>([]);

  const {
    data: playbook,
    isLoading: isPlaybookLoading,
    error,
  } = useGetPlaybookQuery(
    {
      member_id: member_id!,
    },
    { skip: !!currentPlaybook },
  );

  useNotFoundRedirect({ error });

  useEffect(() => {
    const el = document.body;
    if (el) {
      const handleScroll = () => {
        const { top } = el.getBoundingClientRect();
        setShowScrollTop(top <= -54);
      };

      window.addEventListener("scroll", handleScroll);

      return () => window.removeEventListener("scroll", handleScroll);
    }
  }, []);

  if (isPlaybookLoading || (!playbook && !currentPlaybook)) {
    return <CustomLoader />;
  }

  const {
    description,
    lead_details,
    amnesia_best_practices,
    company_type,
    crm_notes,
    do_dont,
    lead_company,
    lead_name,
    outbound_sequence,
    first_call,
    read_first,
    outreach_research,
  } = playbook! || currentPlaybook!;

  const setTopicHandler = (topic: string) => {
    setTopicsList((prev) => [...prev, topic]);
  };

  return (
    <Stack
      sx={{
        px: { sm: 8, md: 8, lg: 10, xl: 20 },
        pt: 4,
        minHeight: "Calc(100vh - 74px)",
      }}
      gap="40px"
    >
      <Stack gap="24px">
        <Stack flexDirection="row" gap="40px" position="sticky">
          <PlaybookNavigationBlock
            topicsList={topicsList}
            isPublicPage={true}
          />
          <Stack gap="48px" flexGrow={1} paddingBottom="48px">
            <PlaybookHeader
              isPublic={true}
              isFixed={showScrollTop}
              playbook={currentPlaybook || playbook}
              companyType={company_type}
              companyName={lead_company}
              leadName={lead_name}
              member_id={member_id!}
              campaign_id={campaign_id!}
              isMock={!!currentPlaybook}
            />
            <TextBlock text={description} />
            {read_first && (
              <ReadFirstBlock
                {...read_first}
                lead_company={lead_company}
                setTopic={setTopicHandler}
              />
            )}
            {do_dont && <DoDontBlock setTopic={setTopicHandler} />}
            {lead_details && (
              <LeadDetailsBlock
                leadDetails={lead_details}
                setTopic={setTopicHandler}
              />
            )}

            {outreach_research && (
              <PreOutreachResearchBlock
                {...outreach_research}
                lead_company={lead_company}
                setTopic={setTopicHandler}
              />
            )}

            {outbound_sequence && (
              <OutboundSequenceBlock
                {...outbound_sequence}
                setTopic={setTopicHandler}
              />
            )}

            {first_call && (
              <FirstCallScriptBlock
                {...first_call}
                setTopic={setTopicHandler}
              />
            )}

            {amnesia_best_practices && (
              <LeadAmnesiaBestPracticesBlock
                {...amnesia_best_practices}
                setTopic={setTopicHandler}
              />
            )}
            {crm_notes && (
              <CRMNotes list={crm_notes} setTopic={setTopicHandler} />
            )}
          </Stack>
        </Stack>
      </Stack>
      {showScrollTop && <ToTopBtn />}
    </Stack>
  );
};

export default PublicPlaybookPage;
